// A load that always answers with data at least as new as the call (release 1016 integration fix).
// lib/saved.ts used to hand a caller the load already in flight. A Save that landed while an earlier load was running
// (the second of two quick saves, or Build around this right after another reload) got that older answer, so Saved and the
// day builder missed the thing just saved (journey J2 "Saved lists what was saved", J3 "Build around this opens the day
// builder"). Now a call made after a write (`after`: a Save just landed) during a load queues exactly one more load after it,
// and every such caller waits for that one. A plain read during a load still joins it (no extra requests on page load:
// the masthead count and the check-in both read Saved at start).
// A change of person (`reset`) makes the running load's answer void: it is never applied for the new person (B-161).
//
// No imports on purpose: tests/latest_load_test.ts runs it under Deno.

export type Loader<O> = {
  /** Load now. During a load: join it, or with `after` (a write just landed) load once more right after it, so the answer
   *  is at least as new as the call. */
  run: (opts: O, after?: boolean) => Promise<void>
  /** The person changed: drop what is running or queued (their answers are not applied) and start fresh on the next run. */
  reset: () => void
  /** Is another load queued behind the running one? (keep the loading state on between the two) */
  more: () => boolean
}

/**
 * `load(opts, live)` does the work; it must call `live()` before applying its answer and skip applying when it is false.
 * `merge` combines the options of calls queued behind a running load (default: the newest call's).
 */
export function latestLoad<O>(load: (opts: O, live: () => boolean) => Promise<void>, merge: (a: O, b: O) => O = (_a, b) => b): Loader<O> {
  let gen = 0
  let running: Promise<void> | null = null
  let queued: { opts: O; p: Promise<void> } | null = null

  const start = (opts: O): Promise<void> => {
    const mine = gen
    const box: { p: Promise<void> | null } = { p: null }
    box.p = (async () => {
      try { await load(opts, () => mine === gen) } finally { if (mine === gen && running === box.p) running = null }
    })()
    running = box.p
    return box.p
  }

  const run = (opts: O, after = false): Promise<void> => {
    if (!running) return start(opts)
    if (!after) return queued?.p ?? running
    if (queued) { queued.opts = merge(queued.opts, opts); return queued.p }
    const prev = running
    const mine = gen
    const q: { opts: O; p: Promise<void> } = { opts, p: Promise.resolve() }
    q.p = prev.catch(() => {}).then(() => {
      if (mine !== gen) return run(q.opts, true) // the person changed meanwhile: a fresh load for them
      if (queued === q) queued = null
      return start(q.opts)
    })
    queued = q
    return q.p
  }

  const reset = () => { gen++; running = null; queued = null }
  return { run, reset, more: () => queued !== null }
}
